/**
 * La vista de dirección.
 *
 * **No es el tablero ejecutivo con menos cosas.** El ejecutivo es táctico: tiene cola
 * editable, notas, cambio de prioridad inline — sirve para *trabajar*. Éste sirve para
 * *decidir*, y por eso no tiene una sola acción: nada que tocar, nada que romper, nada que
 * distraiga de la única pregunta que importa en una reunión de dirección.
 *
 * Contesta cinco preguntas, y ninguna más. El criterio viene de cómo se gestiona un área de
 * tecnología o una mesa de ayuda, no del catálogo de lo que el sistema sabe calcular:
 *
 *   1. ¿La demanda supera a la capacidad?   entradas vs. salidas -> backlog neto
 *   2. ¿Cumplimos lo prometido?             SLA de atención y de resolución, por mes; y en lo
 *                                           planificado, la fecha que se comprometió (2026-10-09)
 *   3. ¿Se trabaja o se interrumpe?         traspasos, repriorizaciones, altas manuales
 *   4. ¿En qué se va el trabajo?            categorías, y cuánto queda sin clasificar
 *   5. ¿Sirvió?                             conformidad de quien pidió
 *
 * **Todo sale de `pmo/cubo`, que ya existía.** Cero endpoints nuevos: la vista de dirección
 * resultó ser un problema de lectura, no de cálculo. Lo único que hizo falta agregar fue la
 * dimensión `mes_cierre` — sin salidas no hay backlog, y el backlog es la cifra que dice si
 * el área alcanza.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { analyticsAPI, capsulesAPI } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import { Loader2, RefreshCw, TrendingUp } from 'lucide-react';
import { ChartCard, CustomTooltip, COLOR_SEMAFORO, colorDeSerie } from '@/components/charts';
import CifraConTendencia from '@/components/direccion/CifraConTendencia';
import HelpButton from '@/components/HelpButton';

const VENTANAS = [
  { meses: 3, label: '3 meses' },
  { meses: 6, label: '6 meses' },
  { meses: 12, label: '12 meses' },
];

//: Desde qué porcentaje de casos sin clasificar el número se pinta en rojo.
//:
//: Quince: por encima de eso el vocabulario quedó corto o el clasificador se rompió, y las dos
//: cosas se arreglan. Estaba escrito a mano en el JSX y **no se declaraba en ninguna parte de la
//: pantalla**, así que el número se pintaba y nadie sabía contra qué.
const UMBRAL_DE_SIN_CLASIFICAR = 15;

//: Cuántos días mira la capacidad, hacia adelante. Cuatro semanas: lo que un director puede
//: cambiar —contratar, recortar alcance, repriorizar— no se decide por el día de mañana, y más allá
//: del mes el plan todavía no está cargado y el número diría que hay capacidad de sobra.
const DIAS_DE_LA_VENTANA_DE_CAPACIDAD = 28;

/** La fecha de dentro de N días, en ISO. */
function enDiasHabiles(dias) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return d.toISOString().slice(0, 10);
}

/** El primer día del mes, N meses atrás, en ISO. */
function desdeHace(meses) {
  const d = new Date();
  d.setMonth(d.getMonth() - (meses - 1));
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

/**
 * Las filas del cubo, ordenadas por clave y sin el bucket.
 *
 * **Dos trampas del contrato, las dos aprendidas leyendo el servicio.** `armar_cubo` ordena
 * por `-len(tickets)`, así que una serie mensual sale desordenada y el gráfico saldría con
 * los meses mezclados. Y la fila `Otros (N)` no es un mes: en una serie temporal no tiene
 * dónde ir.
 */
function porMes(cubo) {
  return (cubo?.filas || [])
    .filter((f) => !f.es_bucket && /^\d{4}-\d{2}$/.test(f.clave))
    .sort((a, b) => a.clave.localeCompare(b.clave));
}

const mesCorto = (clave) => {
  const [a, m] = String(clave).split('-');
  return `${['', 'ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][+m] || m}/${a.slice(2)}`;
};

//: El intent al que cae lo que el clasificador no supo clasificar. Es un id de plataforma
//: (`routing_catalogs.INTENT_FALLBACK_ID`), no vocabulario de una capsula: existe en todas.
const INTENT_SIN_CLASIFICAR = 'SIN_CLASIFICAR';

export default function DireccionPage() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id;

  const [capsulas, setCapsulas] = useState([]);
  const [capsulaId, setCapsulaId] = useState('');
  const [meses, setMeses] = useState(12);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    capsulesAPI.list(tenantId)
      .then((r) => {
        const cs = r.data || [];
        setCapsulas(cs);
        if (cs.length && !capsulaId) setCapsulaId(cs[0].capsule_id || cs[0].id);
      })
      .catch(() => setCapsulas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const cargar = useCallback(async () => {
    if (!tenantId || !capsulaId) return;
    setLoading(true);
    setError('');
    const base = { capsule_id: capsulaId, desde: desdeHace(meses) };
    try {
      // Cuatro cortes del mismo endpoint, en paralelo. El cubo ya trae las trece medidas
      // por fila, así que no hace falta pedir una por indicador.
      const [entradas, salidas, categorias, intents, capitulos, capacidad, planificados] = await Promise.all([
        analyticsAPI.pmoCubo({ ...base, rows: 'mes' }),
        // **La ventana del corte de cierres va por fecha de cierre.** Con el mismo `desde` que las
        // entradas, el servidor acotaba los dos por `primer_contacto_at`: todo caso que entro antes
        // de la ventana y cerro adentro **no aparecia como salida**. Barras de cierre bajas y una
        // linea de backlog con pendiente positiva fabricada, en el unico grafico que contesta si el
        // area alcanza — y la decision que habilita es pedir gente.
        analyticsAPI.pmoCubo({ ...base, rows: 'mes_cierre', ventana_por: 'cierre' }),
        analyticsAPI.pmoCubo({ ...base, rows: 'categoria' }),
        // `claves_fijas` pide que la fila del intent de fallback **no caiga en el bucket
        // `Otros (N)`**: el cubo recorta a 50 filas por volumen, y con un vocabulario grande y pocos
        // casos sin clasificar esa fila desaparecia — o sea que el indicador que existe para delatar
        // un clasificador roto se apagaba en silencio, justo donde mas falta hacia.
        analyticsAPI.pmoCubo({ ...base, rows: 'intent', cols: 'mes', claves_fijas: INTENT_SIN_CLASIFICAR }),
        analyticsAPI.pmoCubo({ ...base, rows: 'capitulo' }),
        // **La capacidad, que es la otra mitad de «¿alcanza el equipo?».** Las barras de arriba
        // dicen cuánto entra y cuánto sale; esto dice cuánto puede poner el equipo. Vivía sólo en
        // el Gantt, que un director no abre, y es la decisión que esta pantalla habilita: pedir
        // gente, o dejar de prometer. Va de hoy hacia adelante y no sobre la ventana histórica: la
        // capacidad del mes pasado no se puede cambiar.
        analyticsAPI.pmoCapacidad({
          capsule_id: capsulaId,
          desde: new Date().toISOString().slice(0, 10),
          hasta: enDiasHabiles(DIAS_DE_LA_VENTANA_DE_CAPACIDAD),
        }),
        // **Lo planificado, por gerencia y sin ventana** (2026-10-09). Sin `desde`, porque lo que
        // espera planificación puede haber entrado hace años —la planilla trae casos de 2016— y
        // con la ventana de arriba desaparecería justo lo que lleva más tiempo esperando.
        analyticsAPI.pmoCubo({ capsule_id: capsulaId, rows: 'gerencia' }),
      ]);
      setDatos({
        entradas: entradas.data, salidas: salidas.data,
        categorias: categorias.data, intents: intents.data,
        capitulos: capitulos.data, capacidad: capacidad.data,
        planificados: planificados.data,
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar la vista de dirección');
      setDatos(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, capsulaId, meses]);

  useEffect(() => { cargar(); }, [cargar]);

  /** La serie mensual con todo junto: entradas, salidas y backlog acumulado. */
  const serie = useMemo(() => {
    if (!datos) return [];
    const ent = porMes(datos.entradas);
    const sal = Object.fromEntries(porMes(datos.salidas).map((f) => [f.clave, f.total.unidades]));
    let acumulado = 0;
    return ent.map((f) => {
      const entradas = f.total.unidades;
      const salidas = sal[f.clave] || 0;
      acumulado += entradas - salidas;
      return {
        mes: f.clave,
        etiqueta: mesCorto(f.clave),
        entradas,
        salidas,
        backlog: acumulado,
        // **La linea de tendencia usa lo que ya cerro.** Para un caso abierto el reloj corre contra
        // `ahora`, asi que su semaforo es una proyeccion que empeora en cada recarga: el mes en curso
        // salia optimista por construccion y se degradaba solo. Mezclado con los cerrados, cada
        // punto era un porcentaje de dos cosas distintas.
        cumplimiento: f.total.sla_resolucion_terminadas?.cumplimiento_pct ?? null,
        cumplimiento_medidas: f.total.sla_resolucion_terminadas?.medidas ?? 0,
        // El de todos —incluyendo los abiertos— se guarda para el KPI de arriba, que mira el stock
        // de hoy y no una tendencia.
        cumplimiento_con_abiertos: f.total.sla_resolucion?.cumplimiento_pct ?? null,
        atencion: f.total.sla_atencion?.cumplimiento_pct ?? null,
        p50: f.total.horas_a_atencion_p50 ?? null,
        traspasos: f.total.traspasos || 0,
        repriorizaciones: f.total.repriorizaciones || 0,
        altas: f.total.altas_manuales?.n || 0,
        // **El denominador del unico indicador que lo trae.** `la_interrupcion.del_universo`
        // devuelve `altas_manuales` con su `sobre` —cuantos casos del periodo— y su propio docstring
        // insiste en que sin denominador no dice nada. Esta pantalla tomaba el `n` y **tiraba el
        // `sobre`**: el numerador del unico indicador diseñado con denominador.
        altas_sobre: f.total.altas_manuales?.sobre || 0,
        reaperturas: f.total.reaperturas || 0,
        // Las cuatro juntas, para poder decir "N eventos" sin apilar cosas que no se suman.
        interrupciones: (f.total.traspasos || 0) + (f.total.repriorizaciones || 0)
          + (f.total.reaperturas || 0),
        unidades: f.total.unidades || 0,
        conformidad: f.total.conformidad?.satisfaccion_p50 ?? null,
        respondidas: f.total.conformidad?.respondidas || 0,
        // **El denominador de la conformidad**, que es el mismo error que la nota de arriba
        // señala sobre las altas manuales, cometido en la tarjeta de al lado: la satisfacción
        // viajaba con su `n` de respuestas y **sin cuántas veces se preguntó**. Un 100 % sobre
        // dos respuestas de cuarenta invitaciones se lee igual que un 100 % de verdad.
        invitadas: f.total.conformidad?.invitadas || 0,
        tasa_de_respuesta: f.total.conformidad?.tasa_de_respuesta_pct ?? null,
        terminadas_sin_invitar: f.total.conformidad?.terminadas_sin_invitar || 0,
        sla: f.total.sla_resolucion,
      };
    });
  }, [datos]);

  const ultimo = serie.length ? serie[serie.length - 1] : null;
  const totales = datos?.entradas?.totales;

  const categorias = useMemo(() => {
    if (!datos) return [];
    return (datos.categorias?.filas || [])
      .filter((f) => !f.es_bucket)
      .slice(0, 8)
      .map((f) => ({
        nombre: f.clave,
        unidades: f.total.unidades,
        cumplimiento: f.total.sla_resolucion?.cumplimiento_pct ?? null,
      }));
  }, [datos]);

  /**
   * Qué porcentaje del mes quedó sin clasificar. El clasificador roto no avisa: sube esto.
   *
   * La fila llega garantizada porque la consulta la pide con `claves_fijas`. Si igual no viene —no
   * hay datos en la ventana— el indicador devuelve `null` y la tarjeta no se dibuja, que es distinto
   * de dibujar un 0 %: no es que no haya casos sin clasificar, es que no hay casos.
   */
  const sinClasificar = useMemo(() => {
    const fila = (datos?.intents?.filas || []).find((f) => f.clave === INTENT_SIN_CLASIFICAR);
    if (!totales?.unidades) return null;
    // Sin fila y con unidades en la ventana: no hubo ninguno sin clasificar. Eso **es** un cero, y
    // es una buena noticia que conviene mostrar — antes se leia igual que "el indicador no existe".
    if (!fila) return 0;
    return Math.round((fila.total.unidades / totales.unidades) * 100);
  }, [datos, totales]);

  /**
   * Lo que vuelve: los capítulos de memoria con más casos.
   *
   * Es la única franja que contesta *por qué* vuelve a pasar algo, y no *de qué* se trata.
   * La categoría dice "puesto de trabajo"; el capítulo dice "el disco se llena por los logs
   * de APEX" — y sobre eso se puede hacer algo.
   */
  const capitulos = useMemo(() => {
    const filas = (datos?.capitulos?.filas || []).filter((f) => !f.es_bucket);
    const sinCapitulo = filas.find((f) => f.clave === '(sin valor)');
    const total = datos?.capitulos?.totales?.unidades || 0;
    return {
      top: filas.filter((f) => f.clave !== '(sin valor)').slice(0, 6)
        .map((f) => ({ nombre: f.clave, unidades: f.total.unidades })),
      // Cuánto del trabajo del área nunca se capitalizó. Es una cifra de dirección por
      // derecho propio: si es alta, se está resolviendo lo mismo una y otra vez sin que
      // quede nada escrito.
      sinCapitularPct: total ? Math.round(((sinCapitulo?.total.unidades || 0) / total) * 100) : null,
    };
  }, [datos]);

  const serieDe = (clave) => serie.map((p) => ({ v: p[clave] }));

  return (
    <div className="p-6 space-y-5 max-w-[1500px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[#09090B] flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-brand-primary" /> Dirección
            <HelpButton section="direccion" />
          </h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Cinco preguntas, con su tendencia. Para trabajar los casos está el tablero ejecutivo.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={capsulaId}
            onChange={(e) => setCapsulaId(e.target.value)}
            className="border border-brand-border rounded-sm h-8 px-2 text-xs"
            data-testid="direccion-capsula"
          >
            {capsulas.map((c) => (
              <option key={c.capsule_id || c.id} value={c.capsule_id || c.id}>{c.name || c.capsule_id || c.id}</option>
            ))}
          </select>
          <div className="flex border border-brand-border rounded-sm overflow-hidden">
            {VENTANAS.map((v) => (
              <button
                key={v.meses}
                onClick={() => setMeses(v.meses)}
                className={`text-xs px-2.5 h-8 ${meses === v.meses ? 'bg-brand-primary text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={cargar} disabled={loading}>
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border border-brand-danger bg-red-50 rounded-sm p-3 text-xs text-brand-danger">{error}</Card>
      )}

      {loading && !datos ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
      ) : !serie.length ? (
        /* Un vacío que dice por qué, y qué haría que deje de estarlo. Un tablero en blanco
           se lee como "está roto"; éste se lee como "todavía no hay de qué hablar". */
        <Card className="border border-brand-border rounded-sm p-10 text-center">
          <p className="text-sm text-zinc-600">Todavía no hay casos en esta ventana.</p>
          <p className="text-xs text-zinc-400 mt-1">
            La vista de dirección mide tendencias: necesita al menos dos meses con movimiento
            para decir algo. Probá con una ventana más larga, o esperá a que entren casos.
          </p>
        </Card>
      ) : (
        <>
          {/* 1 · Las cinco cifras, cada una con su tendencia.
              **La quinta entro el 2026-10-08 y es el denominador de la cuarta**: la satisfaccion
              sin la tasa de respuesta al lado se lee como si todos hubieran contestado. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {/* **Tres cosas en una tarjeta, y la nota describía la que no era el número.** El
                valor grande es el stock de abiertos **de ahora**; la línea de abajo es el backlog
                acumulado del período —que arranca en cero al inicio de la ventana, no en el stock
                real—; y la nota hablaba sólo de la línea. Ahora la nota dice las dos cosas. */}
            <CifraConTendencia
              etiqueta="Casos abiertos" valor={totales?.abiertas} serie={serieDe('backlog')}
              mejorEs="bajo"
              nota="Abiertos hoy. La línea es el backlog del período, y arranca en cero"
            />
            {/* **«Cumplimiento» significaba dos cosas distintas y las dos vivían acá.** Ésta mide
                sólo los **cerrados** —un hecho consumado—; el «Cumplimiento» del cubo mezcla
                abiertos, cuyo reloj corre contra ahora y empeora en cada recarga. Peor: el color de
                las barras de «¿En qué se va el trabajo?» usaba la versión con abiertos mientras
                esta línea usaba la de cerrados, con el mismo nombre. Los dos nombres ahora dicen
                cuál es cuál. */}
            <CifraConTendencia
              etiqueta="Cumplimiento (cerrados)" valor={ultimo?.cumplimiento} sufijo="%"
              serie={serieDe('cumplimiento')} mejorEs="alto"
              nota="Casos ya cerrados que tenían compromiso y llegaron"
            />
            {/* **La nota describía la mediana, que esta tarjeta no muestra.** El valor es un
                porcentaje de cumplimiento; *«cuánto tarda el área en dar señales de vida»* es
                `horas_a_atencion_p50`, una medida que la pantalla calcula y nunca pinta. */}
            <CifraConTendencia
              etiqueta="Atención en plazo" valor={ultimo?.atencion} sufijo="%"
              serie={serieDe('atencion')} mejorEs="alto"
              nota={ultimo?.p50 != null
                ? `Casos atendidos dentro del plazo. La mediana es ${ultimo.p50} h hábiles`
                : 'Casos atendidos dentro del plazo comprometido'}
            />
            {/* **Con el `%` y diciendo contra qué.** Salía como un `25` pelado —los otros dos
                sí llevaban sufijo— y quien había contestado "2 de 5" no reconocía su propia
                respuesta en ese número. La cifra está **normalizada a 0-100** a propósito: una
                cápsula pregunta sobre 5 y la de al lado sobre 10, y promediar los crudos
                mezclaría un 3 que era "regular" con un 3 que era "malo". Pero si el tablero no
                dice contra qué, el número no se puede leer. */}
            <CifraConTendencia
              etiqueta="Conformidad" valor={ultimo?.conformidad} sufijo="%"
              serie={serieDe('conformidad')} mejorEs="alto"
              vacio="nadie contestó"
              nota={ultimo?.respondidas
                ? `${ultimo.respondidas} respuesta${ultimo.respondidas === 1 ? '' : 's'} este mes · 0 % es lo peor, 100 % lo mejor`
                : 'sin respuestas'}
            />
            {/* **La tasa de respuesta, que es la que vuelve honesta a la de al lado.** Un 100 %
                de satisfacción sobre 1 de 40 respuestas es el número más engañoso que puede
                mostrar un tablero — y es el que mira Dirección. De cara a ISO 9001 también es el
                que importa: la norma pide medir la satisfacción del cliente, y una medición sobre
                el 5 % de los casos no mide la satisfacción, mide quién contesta encuestas.

                `terminadas_sin_invitar` se dice en la nota porque delata un circuito **roto**: la
                encuesta prendida y nadie preguntando. Sin eso, una tasa del 100 % sobre dos
                invitaciones de cuarenta casos terminados se lee como un éxito. */}
            <CifraConTendencia
              etiqueta="Contestaron" valor={ultimo?.tasa_de_respuesta} sufijo="%"
              serie={serieDe('tasa_de_respuesta')} mejorEs="alto"
              vacio="no se preguntó"
              nota={ultimo?.invitadas
                ? `${ultimo.respondidas} de ${ultimo.invitadas} que se preguntó`
                  + (ultimo.terminadas_sin_invitar
                    ? ` · ${ultimo.terminadas_sin_invitar} caso${ultimo.terminadas_sin_invitar === 1 ? '' : 's'} terminó sin preguntarle a nadie`
                    : '')
                : 'ninguna invitación este mes'}
            />
          </div>

          {/* 2 · Demanda contra capacidad */}
          <ChartCard
            title="¿La demanda supera a la capacidad?"
            subtitle="Barras: casos que entraron y que se cerraron cada mes. Línea: el backlog que queda."
          >
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={serie}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="etiqueta" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="entradas" name="Entraron" fill={colorDeSerie(0)} />
                <Bar dataKey="salidas" name="Se cerraron" fill={colorDeSerie(1)} />
                <Line type="monotone" dataKey="backlog" name="Backlog" stroke="#A31C15" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>

            {/* **La capacidad, que es la otra mitad de la pregunta del título.** Las barras dicen
                cuánto entra y cuánto sale —el pasado—; esto dice cuánto puede poner el equipo las
                próximas cuatro semanas. Vivía sólo en el Gantt, que un director no abre, y es la
                decisión que esta pantalla habilita: pedir gente, o dejar de prometer.

                Las horas comprometidas son las **de las tareas con fecha**: lo que todavía no se
                planificó no pesa, y por eso el número puede parecer bajo. Lo dice al lado. */}
            {(() => {
              const r = datos?.capacidad?.resumen;
              if (!r) return null;
              if (!r.personas) {
                return (
                  <p className="text-[11px] text-zinc-500 mt-3 pt-3 border-t border-brand-border">
                    Nadie tiene tareas con fecha en las próximas cuatro semanas, así que no hay
                    capacidad comprometida que medir. No es que el equipo esté libre: es que el plan
                    no está cargado.
                  </p>
                );
              }
              const apretado = r.pct != null && r.pct > 85;
              return (
                <div className="mt-3 pt-3 border-t border-brand-border" data-testid="direccion-capacidad">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                    Las próximas cuatro semanas
                  </p>
                  <p className="text-sm">
                    <b className={apretado ? 'text-brand-danger' : 'text-[#09090B]'}>
                      {r.pct == null ? '—' : `${r.pct} %`}
                    </b>
                    <span className="text-zinc-600">
                      {' '}de la capacidad del equipo ya está comprometida —{' '}
                      <span className="font-mono">{r.horas_planificadas}</span> h de{' '}
                      <span className="font-mono">{r.horas_de_capacidad}</span> h, entre{' '}
                      {r.personas} persona{r.personas === 1 ? '' : 's'} y {r.dias_laborables} días
                      hábiles.
                    </span>
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-1">
                    Sólo cuenta lo que tiene fecha: lo que todavía no se planificó no pesa acá.
                    {r.personas_con_conflicto > 0 && (
                      <>
                        {' '}
                        <b className="text-brand-danger">
                          {r.personas_con_conflicto} persona{r.personas_con_conflicto === 1 ? '' : 's'}
                        </b>{' '}
                        ya está sobreasignada algún día del período.
                      </>
                    )}
                  </p>
                </div>
              );
            })()}
          </ChartCard>

          {/* 2b · **Lo prometido en lo planificado** (2026-10-09). Un pedido planificado no tiene SLA:
              su compromiso es la fecha que se fija al planificarlo. El dato se guardaba desde que
              existe ese modo y ningún tablero lo miraba. Va como tabla y no como gráfico: son pocas
              gerencias y cuatro preguntas distintas, y una barra por pregunta escondería los
              denominadores, que acá son la mitad de la respuesta. */}
          {(() => {
            const cubo = datos.planificados;
            const total = cubo?.totales?.compromiso_planificado;
            const filas = (cubo?.filas || [])
              .map((f) => ({ clave: f.clave, m: f.total?.compromiso_planificado }))
              .filter((f) => f.m && f.m.planificados > 0);
            if (!total || !total.planificados) return null;
            const celda = (v, sufijo = '') => (v == null ? '—' : `${v}${sufijo}`);
            const fila = (clave, m, fuerte = false) => (
              <tr key={clave} className={`border-b border-zinc-100 ${fuerte ? 'font-semibold' : ''}`}>
                <td className="py-1.5 pr-3">{clave}</td>
                <td className="py-1.5 pr-3 tabular-nums">{m.planificados}</td>
                <td className="py-1.5 pr-3 tabular-nums">
                  {celda(m.en_fecha_pct, ' %')}
                  <span className="text-zinc-400 font-normal"> ({m.en_fecha} de {m.terminados_con_fecha})</span>
                </td>
                <td className="py-1.5 pr-3 tabular-nums">{celda(m.atraso_dias_p50, ' d')}</td>
                <td className={`py-1.5 pr-3 tabular-nums ${m.en_curso_vencidos ? 'text-brand-danger' : ''}`}>{m.en_curso_vencidos}</td>
                <td className="py-1.5 pr-3 tabular-nums">
                  {m.reprogramaciones}
                  <span className="text-zinc-400 font-normal"> ({m.reprogramados} casos)</span>
                </td>
                <td className="py-1.5 pr-3 tabular-nums">
                  {m.a_planificar}
                  {m.esperando_dias_p50 != null && <span className="text-zinc-400 font-normal"> · {m.esperando_dias_p50} d</span>}
                </td>
              </tr>
            );
            return (
              <ChartCard
                title="¿Cumplimos lo prometido? En lo planificado"
                subtitle="Mejoras, proyectos y correcciones que se planifican no tienen SLA: se miden contra la fecha que se le comprometió a quien pidió. Todos los casos, sin ventana."
              >
                <div className="overflow-x-auto" data-testid="direccion-planificados">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-brand-border">
                        <th className="py-1.5 pr-3">Gerencia</th>
                        <th className="py-1.5 pr-3">Planificados</th>
                        <th className="py-1.5 pr-3">Entregados en fecha</th>
                        <th className="py-1.5 pr-3">Atraso mediano</th>
                        <th className="py-1.5 pr-3">En curso y vencidos</th>
                        <th className="py-1.5 pr-3">Reprogramaciones</th>
                        <th className="py-1.5 pr-3">Esperando planificación · hace</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.map((f) => fila(f.clave, f.m))}
                      {fila('Total', total, true)}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-zinc-500 mt-1 leading-snug">
                  «Entregados en fecha» cuenta sólo lo que terminó con una fecha comprometida: lo que
                  todavía espera planificación no tiene contra qué medirse, y por eso va en su propia
                  columna con cuánto hace que espera.
                </p>
              </ChartCard>
            );
          })()}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 3 · Cumplimiento en el tiempo */}
            <ChartCard
              title="¿Cumplimos lo prometido?"
              subtitle="Los dos relojes. El de atención suele importar más: es cuánto tarda alguien en contestar."
            >
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
                  <Tooltip content={<CustomTooltip sufijo="%" />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="cumplimiento" name="Resolución (cerrados)" stroke={COLOR_SEMAFORO.ok} strokeWidth={2} connectNulls />
                  <Line type="monotone" dataKey="atencion" name="Atención" stroke={colorDeSerie(0)} strokeWidth={2} connectNulls />
                </LineChart>
              </ResponsiveContainer>
              {/* **El mes en curso no es comparable con los anteriores**, y hasta hoy nada lo decía.
                  La resolución se mide sobre lo que **cerró**, así que un mes recién empezado tiene
                  pocos casos cerrados y su punto se mueve toda la semana. Decir cuántos lo sostienen
                  es la diferencia entre una tendencia y una línea que alguien va a mirar en una
                  reunión. */}
              {ultimo && (
                <p className="text-[10px] text-zinc-500 mt-1 leading-snug">
                  {ultimo.cumplimiento_medidas
                    ? `El último punto sale de ${ultimo.cumplimiento_medidas} caso(s) ya cerrado(s): el mes en curso todavía se mueve.`
                    : 'El mes en curso no tiene casos cerrados con compromiso todavía, así que no tiene punto.'}
                </p>
              )}
            </ChartCard>

            {/* 4 · La interrupción.

                **Las cuatro series estaban apiladas y no se suman.** Un traspaso, una
                repriorización y una reapertura son conteos de **eventos** sobre los casos del mes;
                un alta manual es un conteo de **casos**. Apilarlas daba una barra cuya altura total
                no significaba nada — y encima tapaba la pregunta, que no es «cuánta interrupción
                hay» sino «de qué tipo».

                Las tres que comparten unidad se apilan; las altas manuales van **al lado**, con su
                propio eje, porque su denominador es otro y porque es la única de las cuatro que el
                servicio calcula con `sobre`. */}
            <ChartCard
              title="¿Se trabaja o se interrumpe?"
              subtitle="Eventos sobre los casos del mes —cambios de manos, de prioridad, reaperturas— y, aparte, cuántos casos entraron a mano."
            >
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={serie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="traspasos" name="Traspasos" stackId="eventos" fill={colorDeSerie(0)} />
                  <Bar dataKey="repriorizaciones" name="Repriorizaciones" stackId="eventos" fill={colorDeSerie(1)} />
                  <Bar dataKey="reaperturas" name="Reaperturas" stackId="eventos" fill={colorDeSerie(2)} />
                  <Bar dataKey="altas" name="Altas manuales (casos)" fill={colorDeSerie(3)} />
                </BarChart>
              </ResponsiveContainer>
              {ultimo && (
                <p className="text-[10px] text-zinc-500 mt-1 leading-snug">
                  {`Último mes: ${ultimo.interrupciones} evento(s) sobre ${ultimo.unidades} caso(s)`}
                  {ultimo.altas_sobre
                    ? `, y ${ultimo.altas} de ${ultimo.altas_sobre} entraron a mano.`
                    : '.'}
                </p>
              )}
            </ChartCard>
          </div>

          {/* 5 · Dónde se va el trabajo */}
          <ChartCard
            title="¿En qué se va el trabajo?"
            subtitle="Las ocho categorías con más volumen. El color es el cumplimiento proyectado —incluye los casos abiertos, cuyo reloj todavía corre—: verde desde 90 %, ámbar desde 70 %, rojo abajo. Gris es «sin casos con compromiso»."
          >
            <div className="flex items-start gap-6">
              <div className="flex-1 min-w-0">
                <ResponsiveContainer width="100%" height={Math.max(180, categorias.length * 34)}>
                  <BarChart data={categorias} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="nombre" width={190} tick={{ fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="unidades" name="Casos">
                      {categorias.map((c, i) => (
                        /* El color sale del cumplimiento, no de la posición: una categoría
                           chica que incumple siempre importa más que una grande que cumple.

                           **Es el cumplimiento proyectado, no el de cerrados**, y son dos medidas
                           distintas que hasta el 2026-10-05 se llamaban igual: ésta incluye los
                           abiertos —cuyo reloj corre contra ahora, así que empeora en cada
                           recarga— y la línea de «¿Cumplimos lo prometido?» mira sólo lo cerrado.
                           Los dos umbrales, 90 y 70, no estaban declarados en ninguna parte de la
                           pantalla: ahora los dice el subtítulo. */
                        <Cell
                          key={i}
                          fill={c.cumplimiento == null ? '#D4D4D8'
                            : c.cumplimiento >= 90 ? COLOR_SEMAFORO.ok
                            : c.cumplimiento >= 70 ? COLOR_SEMAFORO.risk
                            : COLOR_SEMAFORO.breach}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {sinClasificar != null && (
                /* No es un adorno. Si el clasificador se rompe, el sistema sigue
                   funcionando —los casos se registran igual— y lo único que lo delata es
                   que esto suba. Por eso va acá y no escondido en un corte del cubo. */
                <div className="w-44 shrink-0 border-l border-brand-border pl-5 pt-2">
                  <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Sin clasificar</p>
                  <p className={`text-3xl font-semibold leading-none mt-1 ${sinClasificar > UMBRAL_DE_SIN_CLASIFICAR ? 'text-brand-danger' : 'text-[#09090B]'}`}>
                    {sinClasificar}<span className="text-lg text-zinc-400">%</span>
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
                    Casos que el clasificador no supo ubicar. Si sube, el vocabulario quedó
                    corto o el clasificador se rompió — y no avisa de otra forma.
                    {/* El umbral del rojo no estaba declarado en ninguna parte: el número se
                        pintaba y nadie sabía contra qué. */}
                    {' '}Se pinta en rojo arriba de {UMBRAL_DE_SIN_CLASIFICAR} %.
                  </p>
                </div>
              )}
            </div>
          </ChartCard>
          {/* 6 · Lo que vuelve */}
          <ChartCard
            title="¿Qué vuelve a pasar?"
            subtitle="Los capítulos de memoria con más casos: la causa de fondo, no la categoría."
          >
            {capitulos.top.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-xs text-zinc-500">Todavía no hay capítulos con más de un caso.</p>
                <p className="text-[11px] text-zinc-400 mt-1">
                  Un caso deja capítulo cuando su aprendizaje se extrae y alguien lo aprueba.
                  Sin eso no se puede decir qué se repite.
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-6">
                <div className="flex-1 min-w-0">
                  <ResponsiveContainer width="100%" height={Math.max(150, capitulos.top.length * 34)}>
                    <BarChart data={capitulos.top} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="nombre" width={260} tick={{ fontSize: 10 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="unidades" name="Casos" fill={colorDeSerie(4)} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {capitulos.sinCapitularPct != null && (
                  <div className="w-44 shrink-0 border-l border-brand-border pl-5 pt-2">
                    <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-500">Sin capitalizar</p>
                    <p className="text-3xl font-semibold leading-none mt-1 text-[#09090B]">
                      {capitulos.sinCapitularPct}<span className="text-lg text-zinc-400">%</span>
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
                      Casos que no dejaron nada escrito. Si es alto, el área resuelve lo mismo
                      una y otra vez y el conocimiento se va con la persona.
                    </p>
                  </div>
                )}
              </div>
            )}
          </ChartCard>
        </>
      )}
    </div>
  );
}
